import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Pencil, Flame, Sparkles, Heart, MapPin, Shield, Leaf } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { YouTubeFacade } from '@/components/ui/youtube-facade';
import { PageSeoHead } from '@/components/seo/PageSeoHead';
import heroMountain from '@/assets/about-hero.jpg';

const About = () => {
  const { t, i18n } = useTranslation();
  const isIT = i18n.language === 'it';

  const processSteps = [
    { icon: Pencil, title: t('aboutPage.process.design.title'), description: t('aboutPage.process.design.description') },
    { icon: Flame, title: t('aboutPage.process.casting.title'), description: t('aboutPage.process.casting.description') },
    { icon: Sparkles, title: t('aboutPage.process.finishing.title'), description: t('aboutPage.process.finishing.description') },
    { icon: Heart, title: t('aboutPage.process.assembly.title'), description: t('aboutPage.process.assembly.description') },
  ];

  const values = [
    { icon: MapPin, title: t('aboutPage.madeInItaly'), description: t('aboutPage.madeInItalyDesc') },
    { icon: Shield, title: t('aboutPage.hypoallergenic'), description: t('aboutPage.hypoallergenicDesc') },
    { icon: Leaf, title: t('aboutPage.qualityMaterials'), description: t('aboutPage.qualityMaterialsDesc') },
  ];

  return (
    <Layout>
      <PageSeoHead
        title={isIT
          ? 'Chi Siamo – Andrea Sampaoli, Artigiano del Bronzo | Sampaoli Creazioni'
          : 'About Us – Andrea Sampaoli, Bronze Artisan | Sampaoli'}
        description={isIT
          ? 'Scopri la storia di Andrea Sampaoli e la sua passione per i gioielli artigianali in bronzo. Dal 2004 a Teolo, nel cuore dei Colli Euganei.'
          : 'Discover the story of Andrea Sampaoli and his passion for handcrafted bronze jewelry. Since 2004 in Teolo, in the heart of the Euganean Hills.'}
        path="/about"
      />
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroMountain} alt="Andrea Sampaoli artisan workshop - handcrafted bronze jewelry maker" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/50 via-charcoal/30 to-charcoal/60" />
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="font-body text-xs uppercase tracking-[0.3em] text-cream/80 mb-4 block">
              {t('aboutPage.heroLabel')}
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-ivory mb-6">
              {t('aboutPage.heroTitle')}
            </h1>
            <p className="font-body text-base md:text-lg text-cream/90 max-w-2xl mx-auto leading-relaxed">
              {t('aboutPage.heroSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Biography Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="relative">
              <div className="aspect-video overflow-hidden rounded-lg shadow-xl">
                <YouTubeFacade videoId="Z6kGaWexxx0" title="Sampaoli Creazioni" />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-bronze text-cream px-4 py-2 rounded shadow-lg">
                <span className="text-xs uppercase tracking-wider font-medium">Andrea Sampaoli</span>
                <p className="text-[10px] opacity-80">{t('aboutPage.founder')}</p>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }}>
              <h2 className="font-display text-2xl md:text-3xl text-bronze mb-6">
                {t('aboutPage.bioTitle')}
              </h2>
              <div className="space-y-4 text-muted-foreground text-sm leading-relaxed">
                <p>{t('aboutPage.bio1')}</p>
                <p>{t('aboutPage.bio2')}</p>
                <p>
                  {t('aboutPage.bio3').split('Sampaoli Creazioni').map((part, i, arr) => 
                    i < arr.length - 1 ? (
                      <span key={i}>{part}<span className="text-bronze font-medium">Sampaoli Creazioni</span></span>
                    ) : part
                  )}
                </p>
                <p>{t('aboutPage.bio4')}</p>
                <blockquote className="font-elegant italic text-base text-foreground border-l-4 border-bronze/50 pl-4 mt-6 py-2">
                  {t('aboutPage.bioQuote')}
                </blockquote>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-20 bg-secondary">
        <div className="container mx-auto px-4 max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-14">
            <span className="font-body text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3 block">
              {t('aboutPage.processLabel')}
            </span>
            <h2 className="font-display text-2xl md:text-3xl text-bronze mb-4">
              {t('aboutPage.processTitle')}
            </h2>
            <p className="text-muted-foreground text-sm max-w-xl mx-auto">
              {t('aboutPage.processSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-4 gap-4 lg:gap-6">
            {processSteps.map((step, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }} className="text-center">
                <div className="relative inline-block mb-4">
                  <div className="w-12 h-12 rounded-full bg-background flex items-center justify-center text-bronze border border-border">
                    <step.icon className="w-5 h-5" />
                  </div>
                </div>
                <p className="text-xs text-bronze font-medium mb-1">{index + 1}. <span className="text-foreground">{step.title}</span></p>
                <p className="font-body text-xs text-muted-foreground leading-relaxed hidden md:block">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-14 md:py-20 bg-background">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {values.map((value, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }} className="bg-secondary rounded-xl p-6 border border-border">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-background text-bronze mb-4 border border-border">
                  <value.icon className="w-4 h-4" />
                </div>
                <h3 className="font-heading text-sm font-semibold text-bronze mb-2">{value.title}</h3>
                <p className="font-body text-xs text-muted-foreground leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
